/* eslint-disable sonarjs/cognitive-complexity */
/* eslint-disable no-nested-ternary */
import { createSlice } from '@reduxjs/toolkit';
import { message as toaster } from 'antd';
import { API } from 'api';
import { getUIVersion } from 'utils/router-static-data';

const initialState = {
	approvals: [],
	loading: false,
	error: null,
	pagination: {
		page: 1,
		per_page: 20,
		total: 0,
		total_pages: 0,
	},
	summary: {},
	grouped_by_type: {},
	actionLoading: false,
};

const approvalSliceV2 = createSlice({
	name: 'approvalV2',
	initialState,
	reducers: {
		setLoading: (state, action) => {
			state.loading = action.payload;
		},
		setActionLoading: (state, action) => {
			state.actionLoading = action.payload;
		},
		setApprovals: (state, action) => {
			state.approvals = action.payload;
		},
		setPagination: (state, action) => {
			state.pagination = action.payload;
		},
		setSummary: (state, action) => {
			state.summary = action.payload;
		},
		setGroupedByType: (state, action) => {
			state.grouped_by_type = action.payload;
		},
		setError: (state, action) => {
			state.error = action.payload;
		},
		clearError: (state) => {
			state.error = null;
		},
		updateApprovalStatus: (state, action) => {
			const { approval_id, status, data } = action.payload;
			const index = state.approvals.findIndex((approval) => approval.id === approval_id);
			if (index !== -1) {
				state.approvals[index] = {
					...state.approvals[index],
					status,
					...data,
				};
			}
		},
		removeApproval: (state, action) => {
			state.approvals = state.approvals.filter((approval) => approval.id !== action.payload);
		},
	},
});

export const {
	setLoading,
	setActionLoading,
	setApprovals,
	setPagination,
	setSummary,
	setGroupedByType,
	setError,
	clearError,
	updateApprovalStatus,
	removeApproval,
} = approvalSliceV2.actions;

/**
 * Search Pending Approvals V2
 * @param {Object} params
 * @param {Object} params.filter_by - Filter criteria
 *   - approval_type: String (Enum: ["adhoc_request", "vendor_bank_details", "vehicle_replacement", "client_contract", "vendor_contract", "driver_transfer", "vehicle_transfer"])
 *   - status: String (Enum: ["pending", "approved", "rejected"], default: "pending")
 *   - approver_id: Integer (optional)
 *   - entity_id: Integer (optional)
 *   - requested_by: Integer (optional)
 *   - created_from: String (ISO 8601 date, optional)
 *   - created_to: String (ISO 8601 date, optional)
 * @param {Object} params.pagination - Pagination settings { page, per_page }
 * @param {boolean} params.group_by_type - Group results by approval_type (default: false)
 * @returns {Promise} Object with { approvals, pagination, summary, grouped_by_type }
 */
const ERROR_MESSAGE_FETCH_APPROVALS = 'Failed to fetch approvals';

export const searchPendingApprovals =
	({ filter_by = {}, pagination = { page: 1, per_page: 20 }, group_by_type = false }) =>
	async (dispatch) => {
		try {
			dispatch(setLoading(true));
			dispatch(clearError());

			// Build filter_by object - don't set default approval_type, let it be passed explicitly
			const cleanFilterBy = { ...filter_by };

			// Set default status to "pending" if not provided
			if (!cleanFilterBy.status) {
				cleanFilterBy.status = 'pending';
			}

			// Detect UI version to use appropriate API version
			const isV2UI = getUIVersion() === 'new';

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'pending_approvals',
					filter_by: cleanFilterBy,
					pagination: {
						page: pagination?.page || 1,
						per_page: pagination?.per_page || 20,
					},
					group_by_type: group_by_type || false,
				},
				version: isV2UI ? 'v2' : undefined,
			};

			const response = await API.common(CREDENTIALS);

			if (response.status === 'success') {
				const approvals = response.data.approvals || [];
				const paginationData = response.data.pagination || pagination;
				const summary = response.data.summary || {};
				const groupedByType = response.data.grouped_by_type || {};

				dispatch(setApprovals(approvals));
				dispatch(setPagination(paginationData));
				dispatch(setSummary(summary));
				dispatch(setGroupedByType(groupedByType));

				return {
					approvals,
					pagination: paginationData,
					summary,
					grouped_by_type: groupedByType,
				};
			}
			throw new Error(response.message || ERROR_MESSAGE_FETCH_APPROVALS);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message || error.message || ERROR_MESSAGE_FETCH_APPROVALS;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return {
				approvals: [],
				pagination: {},
				summary: {},
				grouped_by_type: {},
			};
		} finally {
			dispatch(setLoading(false));
		}
	};

/**
 * Handle Adhoc Request Approval (Approve/Reject)
 * @param {Object} params
 * @param {number} params.id - Adhoc request ID to action
 * @param {number} params.approval_id - Approval ID for local list update
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.comments - Optional comments for approval
 * @param {string} params.rejection_reason - Required for rejection
 * @returns {Promise} Object with { success, data, error }
 */
export const handleAdhocRequestApproval =
	({ id, approval_id, action, comments, rejection_reason, status }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());
			const adhocRequestId = id || approval_id;
			if (!adhocRequestId) {
				throw new Error('Adhoc request ID is required');
			}
			const resolvedStatus =
				status ||
				(action === 'reject' ? 'Rejected' : action === 'approve' ? 'Approved' : '');

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'adhoc_request',
					data: {
						id: adhocRequestId,
						status: resolvedStatus,
						...(action === 'approve' && comments ? { comments } : {}),
						...(action === 'reject' && rejection_reason
							? { rejection_remarks: rejection_reason }
							: {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			if (response.status === 'success') {
				toaster.success(response.message || `Adhoc request ${action}d successfully`);

				// Remove the approval from the list since it's no longer pending
				if (approval_id) {
					dispatch(removeApproval(approval_id));
				}

				return { success: true, data: response.data };
			}
			throw new Error(response.message || `Failed to ${action} approval`);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message || error.message || `Failed to ${action} approval`;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Approve Adhoc Request V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID
 * @param {string} params.comments - Optional comments
 * @returns {Promise} Object with { success, data, error }
 */
export const approveAdhocRequest =
	({ id, approval_id, comments, status }) =>
	async (dispatch) =>
		dispatch(
			handleAdhocRequestApproval({
				id,
				approval_id,
				action: 'approve',
				comments,
				status,
			})
		);

/**
 * Reject Adhoc Request V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectAdhocRequest =
	({ id, approval_id, rejection_reason, status }) =>
	async (dispatch) =>
		dispatch(
			handleAdhocRequestApproval({
				id,
				approval_id,
				action: 'reject',
				rejection_reason,
				status,
			})
		);

/**
 * Approve or reject the margin chain on a low-margin intracity adhoc TRIP.
 *
 * Same endpoint and same approval record as the adhoc-request chain - the backend
 * dispatches on approval_type - but the entity is a trip, so there is no adhoc request
 * id here. Which leg it advances (City Manager or VP) is decided server-side from the
 * trip's current stage; the caller never says.
 *
 * @param {Object} params
 * @param {number} params.approval_id - Approval record id, from the trip row
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.rejection_reason - Required when rejecting
 * @returns {Promise} Object with { success, data, error }
 */
export const handleTripMarginApproval =
	({ approval_id, action, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());
			if (!approval_id) {
				throw new Error(ERROR_APPROVAL_ID_REQUIRED);
			}
			if (action === 'reject' && !rejection_reason) {
				throw new Error(ERROR_REJECTION_REASON_REQUIRED);
			}

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'handle_adhoc_request_approval',
					data: {
						approval_id,
						action,
						...(action === 'reject' ? { rejection_reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			if (response.status === 'success') {
				toaster.success(response.message || `Trip ${action}d successfully`);
				return { success: true, data: response.data };
			}
			throw new Error(response.message || `Failed to ${action} trip`);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message || error.message || `Failed to ${action} trip`;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Low-margin intracity adhoc submissions still working through City Manager -> VP.
 *
 * These are adhoc requests, not trips: below 5% margin the trip is not created at all,
 * so there is nothing in trips_v2 to list until the VP approves. The backend filter keys
 * on the stashed trip payload, so requests raised through the ordinary adhoc-request form
 * never appear here.
 */
export const fetchIntracityMarginPending =
	({ limit = 10, offset = 0, query_str } = {}) =>
	async (dispatch) => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'adhoc_request',
				filter_by: {
					status: 'intracity_margin',
					...(query_str && query_str.trim() ? { query_str: query_str.trim() } : {}),
				},
				// The adhoc_request search reads per_page (defaulting to 20) and offset —
				// sending `limit` alone silently over-fetches a 20-row page.
				pagination: { per_page: limit, offset },
			},
			version: 'v2',
		};
		try {
			const response = await API.common(CREDENTIALS);
			return {
				rows: response?.data?.adhoc_requests || [],
				count: response?.data?.pagination?.total || 0,
			};
		} catch (error) {
			// RCTable has no catch on its fetch chain: without this an API error leaves an
			// empty table, no message, and an unhandled rejection in the console.
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.message ||
				'Failed to load pending trips';
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { rows: [], count: 0 };
		}
	};

// Error message constants (shared across approval functions)
const ERROR_APPROVAL_ID_REQUIRED = 'Approval ID is required';
const ERROR_COMMENTS_REQUIRED = 'Comments are required for approval';
const ERROR_REJECTION_REASON_REQUIRED = 'Rejection reason is required for rejection';
const FETCH_APPROVAL_DETAILS_ERROR = 'Failed to fetch approval details';
const APPROVAL_NOT_FOUND_ERROR = 'Approval not found';

/**
 * Get Approval Details V2
 * Endpoint: POST /api/v2/search
 * Search Type: "approval_details"
 *
 * @param {Object} params
 * @param {number|string} params.approval_id - Approval ID (required)
 * @returns {Promise} Object with complete approval details:
 *   - approval: Approval object with id, approval_type, entity_id, entity_data, status, etc.
 *   - entity_details: Complete entity details (client, project, vendor, etc.)
 *   - related_data: Related entities (client, project, vendor objects)
 *   - approval_history: Array of approval status changes
 *
 * Response structure:
 * {
 *   approval: { id, approval_type, entity_id, entity_data, status, requested_by, ... },
 *   entity_details: { id, client_id, client_name, project_id, ... },
 *   related_data: { client: {...}, project: {...}, vendor: {...} },
 *   approval_history: [{ status, changed_at, changed_by, changed_by_name }]
 * }
 */
export const getApprovalDetailsV2 =
	({ approval_id }) =>
	async (dispatch) => {
		try {
			dispatch(setLoading(true));
			dispatch(clearError());

			if (!approval_id) {
				dispatch(setError(ERROR_APPROVAL_ID_REQUIRED));
				toaster.error(ERROR_APPROVAL_ID_REQUIRED);
				return {
					approval: null,
					entity_details: null,
					related_data: {},
					approval_history: [],
				};
			}

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'approval_details',
					filter_by: {
						approval_id: Number(approval_id),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			if (response.status === 'success' && response.data) {
				const result = {
					approval: response.data.approval || null,
					entity_details: response.data.entity_details || null,
					related_data: response.data.related_data || {},
					approval_history: response.data.approval_history || [],
				};

				// Validate that we have at least the approval object
				if (!result.approval) {
					dispatch(setError(APPROVAL_NOT_FOUND_ERROR));
					toaster.error(APPROVAL_NOT_FOUND_ERROR);
					return result;
				}

				return result;
			}

			// Handle error response
			const errorMessage =
				response?.error?.message || response?.message || FETCH_APPROVAL_DETAILS_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);

			return {
				approval: null,
				entity_details: null,
				related_data: {},
				approval_history: [],
			};
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message || error?.message || FETCH_APPROVAL_DETAILS_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);

			return {
				approval: null,
				entity_details: null,
				related_data: {},
				approval_history: [],
			};
		} finally {
			dispatch(setLoading(false));
		}
	};

/**
 * Handle Client Contract Approval (Approve/Reject)
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.comments - Required if action = 'approve'
 * @param {string} params.rejection_reason - Required if action = 'reject'
 * @returns {Promise} Object with { success, data, error }
 * @note approver_id is automatically set from JWT token, do not include in request
 */

// Helper function to validate approval request
const validateApprovalRequest = ({ approval_id, action, comments, rejection_reason }) => {
	if (!approval_id) {
		throw new Error(ERROR_APPROVAL_ID_REQUIRED);
	}
	if (action === 'approve' && !comments) {
		throw new Error(ERROR_COMMENTS_REQUIRED);
	}
	if (action === 'reject' && !rejection_reason) {
		throw new Error(ERROR_REJECTION_REASON_REQUIRED);
	}
};

// Helper function to extract error message
const extractErrorMessage = (error, action) =>
	error?.response?.data?.error?.message ||
	error?.response?.data?.message ||
	error?.message ||
	`Failed to ${action} contract`;

export const handleClientContractApproval =
	({ approval_id, action, comments, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			validateApprovalRequest({ approval_id, action, comments, rejection_reason });

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'handle_client_contract_approval',
					data: {
						approval_id,
						action,
						...(action === 'approve' && comments ? { comments } : {}),
						...(action === 'reject' && rejection_reason ? { rejection_reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || `Failed to ${action} contract`;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || `Client contract ${action}d successfully`);
				dispatch(removeApproval(approval_id));
				return { success: true, data: response.data };
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message || response?.message || `Failed to ${action} contract`;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage = extractErrorMessage(error, action);
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Approve Client Contract V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.comments - Required comments for approval
 * @returns {Promise} Object with { success, data, error }
 */
export const approveClientContract =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(
			handleClientContractApproval({
				approval_id,
				action: 'approve',
				comments,
			})
		);

/**
 * Reject Client Contract V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectClientContract =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(
			handleClientContractApproval({
				approval_id,
				action: 'reject',
				rejection_reason,
			})
		);

/**
 * Handle Vendor Contract Approval (Approve/Reject)
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.comments - Required if action = 'approve'
 * @param {string} params.rejection_reason - Required if action = 'reject'
 * @returns {Promise} Object with { success, data, error }
 * @note approver_id is automatically set from JWT token, do not include in request
 */
export const handleVendorContractApproval =
	({ approval_id, action, comments, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			// Validate approval request
			if (!approval_id) {
				throw new Error('Approval ID is required');
			}
			if (action === 'approve' && !comments) {
				throw new Error('Comments are required for approval');
			}
			if (action === 'reject' && !rejection_reason) {
				throw new Error('Rejection reason is required for rejection');
			}

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'handle_vendor_contract_approval',
					data: {
						approval_id,
						action,
						...(action === 'approve' && comments ? { comments } : {}),
						...(action === 'reject' && rejection_reason ? { rejection_reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || `Failed to ${action} contract`;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || `Vendor contract ${action}d successfully`);
				dispatch(removeApproval(approval_id));
				return { success: true, data: response.data };
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message || response?.message || `Failed to ${action} contract`;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage = extractErrorMessage(error, action);
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Approve Vendor Contract V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.comments - Required comments for approval
 * @returns {Promise} Object with { success, data, error }
 */
export const approveVendorContract =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(
			handleVendorContractApproval({
				approval_id,
				action: 'approve',
				comments,
			})
		);

/**
 * Reject Vendor Contract V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectVendorContract =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(
			handleVendorContractApproval({
				approval_id,
				action: 'reject',
				rejection_reason,
			})
		);

/**
 * Search Invoice Edit Approvals V2
 * Uses vendor_field_changes_list search_type for v2 API
 * @param {Object} params
 * @param {string} params.status - Status filter (pending, approved, rejected)
 * @param {number} params.limit - Limit for pagination
 * @param {number} params.offset - Offset for pagination
 * @returns {Promise} API response
 */
export const searchInvoiceEditApprovalsV2 =
	({ status = 'pending', limit = 20, offset = 0 }) =>
	async (dispatch) => {
		try {
			dispatch(setLoading(true));
			dispatch(clearError());

			// Map status to API format: pending -> pending, approved -> accepted, rejected -> rejected
			const apiStatus = status === 'approved' ? 'accepted' : status;

			const CREDENTIALS = {
				url: '/search',
				data: {
					search_type: 'vendor_field_changes_list',
					filter_by: {
						status: apiStatus,
					},
					limit,
					offset,
				},
				version: 'v2', // v2 API
			};

			const response = await API.common(CREDENTIALS);

			if (response.status === 'success') {
				// Transform v2 response to match component expectations
				const approvals = (response.data || []).map((item) => ({
					id: item.id,
					audit_id: item.id, // Use id as audit_id
					entity_id: item.vendor_bill_id,
					approval_type: 'vendor_bill_changes',
					status: item.status === 'accepted' ? 'approved' : item.status, // Map accepted back to approved
					created_at: item.created_at,
					requested_at: item.created_at,
					updated_at: item.updated_at, // Include updated_at for approved/rejected dates
					updated_by_username: item.updated_by_username, // Include updated_by_username for approved_by/rejected_by
					entity_summary: {
						vendor_bill_no: item.invoice_no,
						vendor_name: item.vendor_name,
						project_name: item.project_name || '',
						initial_invoice_value: item.initial_invoice_value,
						updated_invoice_value: item.updated_invoice_value,
						billing_period: item.billing_period,
					},
					// Also include direct fields for backward compatibility
					vendor_bill_no: item.invoice_no,
					vendor_name: item.vendor_name,
					project_name: item.project_name || '',
					initial_invoice_value: item.initial_invoice_value,
					updated_invoice_value: item.updated_invoice_value,
					billing_period: item.billing_period,
				}));

				const paginationData = {
					page: Math.floor(offset / limit) + 1,
					per_page: limit,
					total: response.meta?.count || 0,
				};

				dispatch(setApprovals(approvals));
				dispatch(setPagination(paginationData));

				return {
					approvals,
					pagination: paginationData,
				};
			}

			throw new Error(response.message || ERROR_MESSAGE_FETCH_APPROVALS);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message || error.message || ERROR_MESSAGE_FETCH_APPROVALS;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			dispatch(setApprovals([]));
			dispatch(setPagination({ page: 1, per_page: 20, total: 0 }));
			return {
				approvals: [],
				pagination: {
					page: Math.floor(offset / limit) + 1,
					per_page: limit,
					total: 0,
				},
			};
		} finally {
			dispatch(setLoading(false));
		}
	};

/**
 * Get Invoice Edit Approval Details V2
 * Uses vendor_field_changes_list search_type with vendor_bill_id filter
 * @param {Object} params
 * @param {number} params.vendor_bill_id - Vendor bill ID (required)
 * @returns {Promise} API response with approval details including changes and image_keys
 */
export const getInvoiceEditApprovalDetailsV2 =
	({ vendor_bill_id }) =>
	async () => {
		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_field_changes_list',
				filter_by: {
					vendor_bill_id,
				},
				limit: 50,
				offset: 0,
			},
			version: 'v2', // v2 API
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response.status === 'success') {
				// Return the first item from the data array (should be the pending approval)
				const approvalData = response.data?.[0] || null;
				return {
					status: 'success',
					data: approvalData,
				};
			}
			return {
				status: 'error',
				message: response.message || 'Failed to fetch approval details',
			};
		});
	};

/**
 * Get Vendor Bill Field Changes History V2 (Audit Trail)
 * Uses vendor_bill_field_changes search_type
 * @param {Object} params
 * @param {number} params.vendor_bill_id - Vendor bill ID (required)
 * @param {string} params.status - Optional filter by status (pending, accepted, rejected)
 * @param {Object} params.pagination - Optional pagination { page, per_page }
 * @returns {Promise} API response with audit history
 */
export const getVendorBillFieldChangesHistoryV2 =
	({ vendor_bill_id, status, pagination = { page: 1, per_page: 20 } }) =>
	async () => {
		const filterBy = {
			id: vendor_bill_id,
		};

		if (status) {
			filterBy.status = status;
		}

		const CREDENTIALS = {
			url: '/search',
			data: {
				search_type: 'vendor_bill_field_changes',
				filter_by: filterBy,
				pagination: {
					page: pagination.page || 1,
					per_page: pagination.per_page || 20,
				},
			},
			version: 'v2', // v2 API
		};

		return API.common(CREDENTIALS).then((response) => {
			if (response.status === 'success') {
				return {
					status: 'success',
					data: response.data || [],
					meta: response.meta || { count: 0 },
				};
			}
			return {
				status: 'error',
				message: response.message || 'Failed to fetch edit history',
				data: [],
				meta: { count: 0 },
			};
		});
	};

/**
 * Accept Vendor Bill Changes V2
 * @param {Object} params
 * @param {number} params.audit_id - Audit ID (required)
 * @returns {Promise} Object with { success, data, error }
 */
export const acceptVendorBillChanges =
	({ audit_id }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			if (!audit_id) {
				throw new Error('Audit ID is required');
			}

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'vendor_bill_changes_accept',
					data: {
						audit_id,
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			const ERROR_MESSAGE_ACCEPT = 'Failed to accept vendor bill changes';

			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || ERROR_MESSAGE_ACCEPT;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || 'Vendor bill changes accepted successfully');
				return { success: true, data: response.data };
			}

			const errorMessage =
				response?.error?.message || response?.message || ERROR_MESSAGE_ACCEPT;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message ||
				error.message ||
				'Failed to accept vendor bill changes';
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Reject Vendor Bill Changes V2
 * @param {Object} params
 * @param {number} params.audit_id - Audit ID (required)
 * @param {string} params.reason - Optional rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectVendorBillChanges =
	({ audit_id, reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			if (!audit_id) {
				throw new Error('Audit ID is required');
			}

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'vendor_bill_changes_reject',
					data: {
						audit_id,
						...(reason ? { reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			const ERROR_MESSAGE_REJECT = 'Failed to reject vendor bill changes';

			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || ERROR_MESSAGE_REJECT;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || 'Vendor bill changes rejected successfully');
				return { success: true, data: response.data };
			}

			const errorMessage =
				response?.error?.message || response?.message || ERROR_MESSAGE_REJECT;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage =
				error?.response?.data?.message ||
				error.message ||
				'Failed to reject vendor bill changes';
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Handle Vendor Bank Details Approval (Approve/Reject)
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.comments - Required if action = 'approve'
 * @param {string} params.rejection_reason - Required if action = 'reject'
 * @returns {Promise} Object with { success, data, error }
 * @note approver_id is automatically set from JWT token, do not include in request
 */
export const handleVendorBankDetailsApproval =
	({ approval_id, action, comments, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			// Validate approval request
			validateApprovalRequest({ approval_id, action, comments, rejection_reason });

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'handle_vendor_bank_details_approval',
					data: {
						approval_id: Number(approval_id),
						action,
						...(action === 'approve' && comments ? { comments } : {}),
						...(action === 'reject' && rejection_reason ? { rejection_reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					`Failed to ${action} vendor bank details`;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || `Vendor bank details ${action}d successfully`);
				dispatch(removeApproval(approval_id));
				return { success: true, data: response.data };
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message ||
				response?.message ||
				`Failed to ${action} vendor bank details`;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage = extractErrorMessage(error, action);
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Approve Vendor Bank Details V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.comments - Required comments for approval
 * @returns {Promise} Object with { success, data, error }
 */
export const approveVendorBankDetails =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(
			handleVendorBankDetailsApproval({
				approval_id,
				action: 'approve',
				comments,
			})
		);

/**
 * Reject Vendor Bank Details V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectVendorBankDetails =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(
			handleVendorBankDetailsApproval({
				approval_id,
				action: 'reject',
				rejection_reason,
			})
		);

/**
 * Driver / Vehicle document verification (Control Tower and Admin).
 * Comments are optional on approve; a reason is required on reject.
 */
const createVerificationApprovalThunk =
	(updateType, label) =>
	({ approval_id, action, comments, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			if (!approval_id) throw new Error('approval_id is required');
			if (!['approve', 'reject'].includes(action)) {
				throw new Error("action must be 'approve' or 'reject'");
			}
			if (action === 'reject' && !String(rejection_reason || '').trim()) {
				throw new Error(ERROR_REJECTION_REASON_REQUIRED);
			}

			const response = await API.common({
				url: '/update',
				data: {
					update_type: updateType,
					data: {
						approval_id: Number(approval_id),
						action,
						...(action === 'approve' && comments?.trim()
							? { comments: comments.trim() }
							: {}),
						...(action === 'reject'
							? { rejection_reason: rejection_reason.trim() }
							: {}),
					},
				},
				version: 'v2',
			});

			if (response.status === 'success') {
				toaster.success(response.message || `${label} documents ${action}d`);
				dispatch(removeApproval(approval_id));
				return { success: true, data: response.data };
			}
			throw new Error(
				response?.error?.message || response?.message || `Failed to ${action} ${label}`
			);
		} catch (error) {
			const errorMessage = extractErrorMessage(error, action);
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

export const handleDriverApproval = createVerificationApprovalThunk(
	'handle_driver_approval',
	'driver'
);
export const handleVehicleApproval = createVerificationApprovalThunk(
	'handle_vehicle_approval',
	'vehicle'
);

export const approveDriver =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(handleDriverApproval({ approval_id, action: 'approve', comments }));

export const rejectDriver =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(handleDriverApproval({ approval_id, action: 'reject', rejection_reason }));

export const approveVehicle =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(handleVehicleApproval({ approval_id, action: 'approve', comments }));

export const rejectVehicle =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(handleVehicleApproval({ approval_id, action: 'reject', rejection_reason }));

/**
 * Handle Vehicle Replacement Approval (Approve/Reject)
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.action - 'approve' or 'reject'
 * @param {string} params.comments - Required if action = 'approve'
 * @param {string} params.rejection_reason - Required if action = 'reject'
 * @returns {Promise} Object with { success, data, error }
 * @note approver_id is automatically set from JWT token, do not include in request
 */
export const handleVehicleReplacementApproval =
	({ approval_id, action, comments, rejection_reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			// Validate approval request
			validateApprovalRequest({ approval_id, action, comments, rejection_reason });

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'handle_vehicle_replacement_approval',
					data: {
						approval_id,
						action,
						...(action === 'approve' && comments ? { comments } : {}),
						...(action === 'reject' && rejection_reason ? { rejection_reason } : {}),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message ||
					response?.message ||
					`Failed to ${action} vehicle replacement`;
				throw new Error(errorMessage);
			}

			if (response.status === 'success') {
				toaster.success(response.message || `Vehicle replacement ${action}d successfully`);
				dispatch(removeApproval(approval_id));
				return { success: true, data: response.data };
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message ||
				response?.message ||
				`Failed to ${action} vehicle replacement`;
			throw new Error(errorMessage);
		} catch (error) {
			const errorMessage = extractErrorMessage(error, action);
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

/**
 * Approve Vehicle Replacement V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.comments - Required comments for approval
 * @returns {Promise} Object with { success, data, error }
 */
export const approveVehicleReplacement =
	({ approval_id, comments }) =>
	async (dispatch) =>
		dispatch(
			handleVehicleReplacementApproval({
				approval_id,
				action: 'approve',
				comments,
			})
		);

/**
 * Reject Vehicle Replacement V2
 * @param {Object} params
 * @param {number} params.approval_id - Approval ID (required)
 * @param {string} params.rejection_reason - Required rejection reason
 * @returns {Promise} Object with { success, data, error }
 */
export const rejectVehicleReplacement =
	({ approval_id, rejection_reason }) =>
	async (dispatch) =>
		dispatch(
			handleVehicleReplacementApproval({
				approval_id,
				action: 'reject',
				rejection_reason,
			})
		);

// Transfer approval constants
const TRANSFER_APPROVAL_ERROR = 'Failed to transfer approvals';
const VALID_APPROVAL_TYPES = [
	'adhoc_request',
	'vendor_bank_details',
	'vehicle_replacement',
	'client_contract',
	'client_contract_edit',
	'vendor_contract',
	'vendor_contract_edit',
	'driver_transfer',
	'vehicle_transfer',
];

// Helper function to validate transfer request
const validateTransferRequest = ({
	from_user_id,
	to_user_id,
	approval_type,
	approval_ids,
	reason,
}) => {
	if (!from_user_id || !to_user_id) {
		throw new Error('Both from_user_id and to_user_id are required');
	}

	if (from_user_id === to_user_id) {
		throw new Error('from_user_id and to_user_id must be different');
	}

	if (!approval_type) {
		throw new Error('approval_type is required');
	}

	if (!VALID_APPROVAL_TYPES.includes(approval_type)) {
		throw new Error(
			`Invalid approval_type. Must be one of: ${VALID_APPROVAL_TYPES.join(', ')}`
		);
	}

	if (!approval_ids || !Array.isArray(approval_ids) || approval_ids.length === 0) {
		throw new Error('approval_ids is required and must be a non-empty array');
	}

	if (!reason || typeof reason !== 'string' || reason.trim().length === 0) {
		throw new Error('reason is required');
	}

	if (reason.length > 500) {
		throw new Error('reason must not exceed 500 characters');
	}
};

/**
 * Transfer Approval
 * Endpoint: POST /api/v2/update
 * Update Type: "transfer_approval"
 *
 * Transfers pending approvals from one user to another.
 *
 * @param {Object} params
 * @param {number} params.from_user_id - Current approver user ID (required)
 * @param {number} params.to_user_id - New approver user ID (required, must be different from from_user_id)
 * @param {string} params.approval_type - Type of approvals to transfer (required)
 *   Enum: ["adhoc_request", "vendor_bank_details", "vehicle_replacement", "client_contract", "vendor_contract", "driver_transfer", "vehicle_transfer"]
 * @param {number[]} params.approval_ids - Array of approval IDs to transfer (required)
 * @param {string} params.reason - Transfer reason (required, max 500 chars)
 * @returns {Promise} Object with { success, data, error }
 *
 * Response structure:
 * {
 *   from_user_id, from_user_name, to_user_id, to_user_name,
 *   approval_type, approvals_transferred, approval_ids,
 *   transfer_id, transferred_at, reason
 * }
 */
export const transferApproval =
	({ from_user_id, to_user_id, approval_type, approval_ids, reason }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			// Validate transfer request
			validateTransferRequest({
				from_user_id,
				to_user_id,
				approval_type,
				approval_ids,
				reason,
			});

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'transfer_approval',
					data: {
						from_user_id,
						to_user_id,
						approval_type,
						approval_ids: approval_ids.map((id) => id),
						reason: reason.trim(),
					},
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || TRANSFER_APPROVAL_ERROR;
				dispatch(setError(errorMessage));
				toaster.error(errorMessage);
				return { success: false, error: errorMessage };
			}

			if (response.status === 'success') {
				const approvalsCount = response.data?.approvals_transferred || 0;
				const successMessage =
					response.message || `${approvalsCount} approvals transferred successfully`;
				toaster.success(successMessage);

				// Remove transferred approvals from current list
				if (response.data?.approval_ids) {
					response.data.approval_ids.forEach((approvalId) => {
						dispatch(removeApproval(approvalId));
					});
				}

				return {
					success: true,
					data: response.data,
				};
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message || response?.message || TRANSFER_APPROVAL_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				TRANSFER_APPROVAL_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

// Helper function to validate bulk transfer request
const validateBulkTransferRequest = ({
	from_user_id,
	to_user_id,
	approval_type,
	project_ids,
	transfer_all,
}) => {
	if (!from_user_id || !to_user_id) {
		throw new Error('Both from_user_id and to_user_id are required');
	}

	if (from_user_id === to_user_id) {
		throw new Error('from_user_id and to_user_id must be different');
	}

	if (!approval_type) {
		throw new Error('approval_type is required');
	}

	if (!VALID_APPROVAL_TYPES.includes(approval_type)) {
		throw new Error(
			`Invalid approval_type. Must be one of: ${VALID_APPROVAL_TYPES.join(', ')}`
		);
	}

	// If transfer_all is false, project_ids must be provided
	if (
		!transfer_all &&
		(!project_ids || !Array.isArray(project_ids) || project_ids.length === 0)
	) {
		throw new Error(
			'project_ids is required and must be a non-empty array when transfer_all is false'
		);
	}
};

/**
 * Bulk Transfer Approvals
 * Endpoint: POST /api/v2/update
 * Update Type: "bulk_transfer_approvals"
 *
 * Transfers all matching pending approvals from one user to another.
 * Useful when approver is unavailable for extended period.
 *
 * @param {Object} params
 * @param {number} params.from_user_id - Current approver user ID (required)
 * @param {number} params.to_user_id - New approver user ID (required, must be different from from_user_id)
 * @param {string} params.approval_type - Type of approvals to transfer (required)
 *   Enum: ["adhoc_request", "vendor_bank_details", "vehicle_replacement", "client_contract", "vendor_contract", "driver_transfer", "vehicle_transfer"]
 * @param {number[]} params.project_ids - Array of project IDs to filter by (required if transfer_all = false)
 * @param {boolean} params.transfer_all - If true, transfers all pending approvals of type (default: false)
 * @returns {Promise} Object with { success, data, error }
 *
 * Response structure:
 * {
 *   from_user_id, to_user_id, approval_type,
 *   approvals_transferred, approval_ids,
 *   transferred_at
 * }
 */
export const bulkTransferApprovals =
	({ from_user_id, to_user_id, approval_type, project_ids, transfer_all = false }) =>
	async (dispatch) => {
		try {
			dispatch(setActionLoading(true));
			dispatch(clearError());

			// Validate bulk transfer request
			validateBulkTransferRequest({
				from_user_id,
				to_user_id,
				approval_type,
				project_ids,
				transfer_all,
			});

			// Build data object
			const data = {
				from_user_id,
				to_user_id,
				approval_type,
				transfer_all: Boolean(transfer_all),
			};

			// Only include project_ids if transfer_all is false
			if (
				!transfer_all &&
				project_ids &&
				Array.isArray(project_ids) &&
				project_ids.length > 0
			) {
				data.project_ids = project_ids.map((id) => id);
			}

			const CREDENTIALS = {
				url: '/update',
				data: {
					update_type: 'bulk_transfer_approvals',
					data,
				},
				version: 'v2',
			};

			const response = await API.common(CREDENTIALS);

			// Check for error status in response first
			if (response.status === 'error') {
				const errorMessage =
					response?.error?.message || response?.message || TRANSFER_APPROVAL_ERROR;
				dispatch(setError(errorMessage));
				toaster.error(errorMessage);
				return { success: false, error: errorMessage };
			}

			if (response.status === 'success') {
				const approvalsCount = response.data?.approvals_transferred || 0;
				const successMessage =
					response.message || `${approvalsCount} approvals transferred successfully`;
				toaster.success(successMessage);

				// Remove transferred approvals from current list
				if (response.data?.approval_ids && Array.isArray(response.data.approval_ids)) {
					response.data.approval_ids.forEach((approvalId) => {
						dispatch(removeApproval(approvalId));
					});
				}

				return {
					success: true,
					data: response.data,
				};
			}

			// Fallback error handling
			const errorMessage =
				response?.error?.message || response?.message || TRANSFER_APPROVAL_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} catch (error) {
			const errorMessage =
				error?.response?.data?.error?.message ||
				error?.response?.data?.message ||
				error?.message ||
				TRANSFER_APPROVAL_ERROR;
			dispatch(setError(errorMessage));
			toaster.error(errorMessage);
			return { success: false, error: errorMessage };
		} finally {
			dispatch(setActionLoading(false));
		}
	};

export default approvalSliceV2.reducer;
